import { z } from 'zod/v4';
import {
  ITariffServiceArea,
  TARIFF_SERVICE_AREA,
  TariffSelectionAttributeValue,
} from './product-group.interfaces';
import { ViaName } from './relation.interfaces';
import { ITicket, TICKET_SCHEMA } from '../cms/ticket/ticket.interfaces';
import {
  ILayoutAssignment,
  LAYOUT_ASSIGNMENTS_SCHEMA,
} from '../cms/sort-layout-assignment.interfaces';

export interface IHusstAdditionalEntry {
  name: string | null;
  externeReferenz: string | null;
  datenTyp: string;
  zusatzeingabeDatentyp: string;
  druckfeld: string;
  speicherort: string;
  kaVerwendung: string;
  optionen: string[] | null;
  druckrelevant: boolean;
  registrierungsrelevant: boolean;
  kaRelevant: boolean;
  minWert: number | null;
  maxWert: number | null;
  optional: boolean;
  maxLaenge: number | null;
  minLaenge: number | null;
  sortOrder: number;
}

export interface ITariffServiceAreas {
  tarifgebiete: ITariffServiceArea[];
}

export interface IHusstSort {
  id: number;
  variablerPreis: boolean;
  tarifgebiet: ITariffServiceArea;
  extReferenz: string;
  sortentyp: string;
  zahlungsart: string;
  schichtabschlussgruppe: string | null;
  name: string;
  kurzName: string;
  anzeigeText: string;
  anzeigeKurzText: string;
  kundendisplayText: string;
  kundendisplayKurzText: string;
  druckText: string;
  druckText2: string | null;
  druckText3: string | null;
  zusatzEingaben: IHusstAdditionalEntry[];
}

export interface IHusstSortGroup {
  name: string;
  sortengruppentyp: string;
  externeReferenz: string | null;
  sortenExterneReferenzen: string[];
}

export interface ISortGroupWithTicketData extends IHusstSortGroup {
  ticketData?: ITicket;
  layoutAssignments?: ILayoutAssignment[];
}
export interface IHusstSystemPayment {
  name: string;
  extReferenz: string;
  bruttoPreis: number;
  mwstSatz: number;
}

export interface IHusstSelectionAttribute {
  name: string;
  werte: string[];
}

export interface IHusstProductDefinitionElement {
  name: string;
  wert: TariffSelectionAttributeValue;
}

export interface IHusstProductDefinitionKey {
  elemente: IHusstProductDefinitionElement[];
}

export interface HusstProductDefinitionPriceLevel {
  tarifgebiet: ITariffServiceArea;
  extReferenz: string;
  name: string;
  kurzName: string;
  druckText: string;
}

export interface HusstPriceLevelValidityOption {
  typ: string;
  datumListe: string[];
  fruehesterGueltiStart: string;
  spaetesterGueltiStart: string;
  vorbelegung: string;
}

export interface HusstProductDefinitionValue {
  preisstufe: HusstProductDefinitionPriceLevel;
  relation: string | null;
  fahrgasttyp: string;
  rabattklasse: string;
  komfortklasse: string;
  entwertung: string;
  anzahlPersonen: number;
  anzahlFahrten: number | null;
  via: ViaName | null;
  ausgabemedium: string;
  uebertragbar: boolean | null;
  gueltigkeitsstartOptionen: HusstPriceLevelValidityOption;
  gueltigText: string;
  zusammenfassungErlaubt: boolean;
  minAnzahlPersonen: number;
  maxAnzahlPersonen: number;
  standardpersonenanzahl: number | null;
  stornierbar: boolean;
  auswahlEingaben: string | null;
  starterAboRegel: string | null;
  vorlaufAnstossprodukte: string[];
  nachlaufAnstossprodukte: string[];
  unterprodukte: string[] | null;
  teilprodukte: string[];
  zusatzWerte: unknown;
  layout: string | null;
  anzahlAbschnitte: number | null;
  kaDaten: string | null;
  tarifgebiet: ITariffServiceArea;
  versionsnummer: string;
  sorte: IHusstSort;
  extReferenz: string | null;
  bruttoPreis: number;
  mwstSatz: number;
  drucktext: string | null;
  einloesbareGutscheine: string[];
  _hash: string;
}

export interface HusstProductGroup {
  name: string;
  auswahlAttribute: IHusstSelectionAttribute[];
  produktDefinitionen: HusstProductDefinition[];
}
export interface IHusstServiceArea {
  idBediengebiet: number;
  name: string;
}

export const HUSST_ADDITIONAL_ENTRY: z.ZodType<IHusstAdditionalEntry> =
  z.object({
    name: z.string().nullable().default(null),
    externeReferenz: z.string().nullable().default(null),
    datenTyp: z.string(),
    zusatzeingabeDatentyp: z.string(),
    druckfeld: z.string(),
    speicherort: z.string(),
    kaVerwendung: z.string(),
    optionen: z.array(z.string()).nullable().default(null),
    druckrelevant: z.boolean(),
    registrierungsrelevant: z.boolean(),
    kaRelevant: z.boolean(),
    minWert: z.number().nullable().default(null),
    maxWert: z.number().nullable().default(null),
    optional: z.boolean(),
    maxLaenge: z.number().int().nullable().default(null),
    minLaenge: z.number().int().nullable().default(null),
    sortOrder: z.number().int(),
  });

export const HUSST_SORT: z.ZodType<IHusstSort> = z.object({
  id: z.number(),
  variablerPreis: z.boolean(),
  tarifgebiet: TARIFF_SERVICE_AREA,
  extReferenz: z.string(),
  sortentyp: z.string(),
  zahlungsart: z.string(),
  schichtabschlussgruppe: z.string().nullable().default(null),
  name: z.string(),
  kurzName: z.string(),
  anzeigeText: z.string(),
  anzeigeKurzText: z.string(),
  kundendisplayText: z.string(),
  kundendisplayKurzText: z.string(),
  druckText: z.string(),
  druckText2: z.string().nullable().default(null),
  druckText3: z.string().nullable().default(null),
  zusatzEingaben: z.array(HUSST_ADDITIONAL_ENTRY),
});

export const HUSST_PRODUCT_DEFINITION_PRICE_LEVEL: z.ZodType<HusstProductDefinitionPriceLevel> =
  z.object({
    tarifgebiet: TARIFF_SERVICE_AREA,
    extReferenz: z.string(),
    name: z.string(),
    kurzName: z.string(),
    druckText: z.string(),
  });

export const HUSST_PRICE_LEVEL_VALIDITY_OPTION: z.ZodType<HusstPriceLevelValidityOption> =
  z.object({
    typ: z.string(),
    datumListe: z.array(z.string()),
    fruehesterGueltiStart: z.string(),
    spaetesterGueltiStart: z.string(),
    vorbelegung: z.string(),
  });

export const HUSST_PRODUCT_DEFINITION_VALUE: z.ZodType<HusstProductDefinitionValue> =
  z.object({
    preisstufe: HUSST_PRODUCT_DEFINITION_PRICE_LEVEL,
    relation: z.string().nullable().default(null),
    fahrgasttyp: z.string(),
    rabattklasse: z.string(),
    komfortklasse: z.string(),
    entwertung: z.string(),
    anzahlPersonen: z.number(),
    anzahlFahrten: z.number().nullable().default(null),
    via: z
      .string()
      .transform(s => s as ViaName)
      .nullable()
      .default(null),
    ausgabemedium: z.string(),
    uebertragbar: z.boolean().nullable().default(null),
    gueltigkeitsstartOptionen: HUSST_PRICE_LEVEL_VALIDITY_OPTION,
    gueltigText: z.string(),
    zusammenfassungErlaubt: z.boolean(),
    minAnzahlPersonen: z.number(),
    maxAnzahlPersonen: z.number(),
    standardpersonenanzahl: z.number().nullable().default(null),
    stornierbar: z.boolean(),
    auswahlEingaben: z.string().nullable().default(null),
    starterAboRegel: z.string().nullable().default(null),
    vorlaufAnstossprodukte: z.array(z.string()),
    nachlaufAnstossprodukte: z.array(z.string()),
    unterprodukte: z.array(z.string()).nullable().default(null),
    teilprodukte: z.array(z.string()),
    zusatzWerte: z.unknown(),
    layout: z.string().nullable().default(null),
    anzahlAbschnitte: z.number().nullable().default(null),
    kaDaten: z.string().nullable().default(null),
    tarifgebiet: TARIFF_SERVICE_AREA,
    versionsnummer: z.string(),
    sorte: HUSST_SORT,
    extReferenz: z.string().nullable().default(null),
    bruttoPreis: z.number(),
    mwstSatz: z.number(),
    drucktext: z.string().nullable().default(null),
    einloesbareGutscheine: z.array(z.string()),
    _hash: z.string(),
  });

export const HUSST_SYSTEM_PAYMENT: z.ZodType<IHusstSystemPayment> = z.object({
  name: z.string(),
  extReferenz: z.string(),
  bruttoPreis: z.number(),
  mwstSatz: z.number(),
});

export const HUSST_SYSTEM_PAYMENTS: z.ZodType<IHusstSystemPayment[]> =
  z.array(HUSST_SYSTEM_PAYMENT);

export const HUSST_PRODUCT_DEFINITION_ELEMENT: z.ZodType<IHusstProductDefinitionElement> =
  z.object({
    name: z.string(),
    wert: z.string().nullable().default(null),
  });

export const HUSST_PRODUCT_DEFINITION_KEY: z.ZodType<IHusstProductDefinitionKey> =
  z.object({
    elemente: z.array(HUSST_PRODUCT_DEFINITION_ELEMENT),
  });

export const HUSST_PRODUCT_DEFINITION: z.ZodType<HusstProductDefinition> =
  z.object({
    key: HUSST_PRODUCT_DEFINITION_KEY,
    value: HUSST_PRODUCT_DEFINITION_VALUE,
  });

export interface HusstProductDefinition {
  key: IHusstProductDefinitionKey;
  value: HusstProductDefinitionValue;
}

export const HUSST_SELECTION_ATTRIBUTE: z.ZodType<IHusstSelectionAttribute> =
  z.object({
    name: z.string(),
    werte: z.array(z.string()),
  });

export const HUSST_PRODUCT_GROUP: z.ZodType<HusstProductGroup> = z.object({
  name: z.string(),
  auswahlAttribute: z.array(HUSST_SELECTION_ATTRIBUTE),
  produktDefinitionen: z.array(HUSST_PRODUCT_DEFINITION),
});

export const HUSST_PRODUCT_GROUPS: z.ZodType<HusstProductGroup[]> =
  z.array(HUSST_PRODUCT_GROUP);

export const HUSST_SORT_GROUP: z.ZodType<IHusstSortGroup> = z.object({
  name: z.string(),
  sortengruppentyp: z.string(),
  externeReferenz: z.string().nullable().default(null),
  sortenExterneReferenzen: z.array(z.string()),
});

export const HUSST_SORT_GROUPS: z.ZodType<IHusstSortGroup[]> =
  z.array(HUSST_SORT_GROUP);

export const HUSST_SORT_GROUP_WITH_TICKET_DATA: z.ZodType<ISortGroupWithTicketData> =
  HUSST_SORT_GROUP.and(
    z.object({
      ticketData: TICKET_SCHEMA.optional(),
      layoutAssignments: LAYOUT_ASSIGNMENTS_SCHEMA.optional(),
    })
  );

export const HUSST_SORT_GROUPS_WITH_TICKET_DATA: z.ZodType<
  ISortGroupWithTicketData[]
> = z.array(HUSST_SORT_GROUP_WITH_TICKET_DATA);

export const TARIFF_SERVICE_AREAS: z.ZodType<ITariffServiceAreas> = z.object({
  tarifgebiete: z.array(TARIFF_SERVICE_AREA),
});

export const HUSST_SERVICE_AREA: z.ZodType<IHusstServiceArea> = z.object({
  idBediengebiet: z.number(),
  name: z.string(),
});

export const HUSST_SERVICE_AREAS: z.ZodType<IHusstServiceArea[]> =
  z.array(HUSST_SERVICE_AREA);
